import { useEffect, useState } from "react";
import { ArrowLeft, Check, Scissors, Upload } from "lucide-react";
import { collection, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { useAuth } from "../components/auth/AuthGate";
import { db } from "../firebase/firebase";
import { submitTailorQuoteRequest } from "../services/tailorQuoteService";
import TailorQuoteContactFields, { emptyQuoteContact, isQuoteContactValid } from "../components/quotes/TailorQuoteContactFields";

const SERVICES = [
  { id: "combo_dress", label: "Combo Dresses", collectionName: "combo_dress_designs", description: "Coordinated outfits for couples, families and groups, with separate measurements for every participant." },
  { id: "saree_to_dress", label: "Saree to Dress", collectionName: "saree_dress_bundles", description: "Transform a saree into a contemporary dress using one of the available model concepts." },
  { id: "embroidery", label: "Custom Embroidery", description: "Share an embroidery reference and requirements for review and quotation." },
];

const galleryImageOf = (item) => typeof item === "string" ? item : item?.websiteUrl || item?.url || item?.imageUrl || "";
const modelImageOf = (model = {}) => model.websiteImageUrl || model.imageUrl || model.image || galleryImageOf(model.images?.[0]);
const imageOf = (item = {}) => item.websiteImageUrl || item.websiteSareeImageUrl || item.imageUrl || item.sareeImageUrl || item.image || modelImageOf(item.models?.[0]) || "";

export default function SpecialStitchingPage() {
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const [service, setService] = useState(SERVICES[0]);
  const [items, setItems] = useState([]);
  const [servicePrices, setServicePrices] = useState({});
  const [selected, setSelected] = useState(null);
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
  const [quoteContact, setQuoteContact] = useState(() => emptyQuoteContact());
  const [notes, setNotes] = useState("");
  const [upload, setUpload] = useState({ data: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [quoteRequestId, setQuoteRequestId] = useState("");

  useEffect(() => {
    setCustomer((current) => ({
      name: current.name || user?.displayName || "",
      phone: current.phone,
      email: current.email || user?.email || "",
    }));
    setQuoteContact((current) => ({
      ...current,
      phone: current.phone || String(user?.phoneNumber || "").replace(/\D/g, "").slice(-10),
    }));
  }, [user]);

  useEffect(() => {
    setSelected(null);
    setMessage("");
    if (!service.collectionName) {
      setItems([]);
      return undefined;
    }
    return onSnapshot(collection(db, service.collectionName), (snapshot) => {
      const values = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }))
        .filter((item) => item.isActive !== false && item.active !== false)
        .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
      setItems(values);
    }, () => setMessage("Unable to load this catalogue."));
  }, [service]);

  useEffect(() => onSnapshot(collection(db, "services"), (snapshot) => {
    const prices = {};
    snapshot.docs.forEach((entry) => {
      const data = entry.data();
      const id = String(data.serviceId || entry.id);
      prices[id] = Number(data.baseCustomerPrice || data.startingPrice || 0);
    });
    setServicePrices(prices);
  }, () => {}), []);

  const chooseUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 8 * 1024 * 1024) {
      setMessage("Choose an image smaller than 8 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setUpload({ data: String(reader.result || ""), name: file.name });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!user) {
      openAuth();
      return;
    }
    const reference = imageOf(selected || {}) || upload.data;
    if (!reference || !customer.name.trim() || !isQuoteContactValid(quoteContact)) {
      setMessage("Select a design and complete the callback details.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const startingPrice = Number(
        selected?.basePrice ||
        selected?.startingPrice ||
        servicePrices[service.id] ||
        0
      );
      const request = await submitTailorQuoteRequest({
        customer: { ...customer, phone: quoteContact.phone },
        ...quoteContact,
        garmentName: selected?.name || service.label,
        designImageDataUrl: reference,
        designFileName: upload.name || `${selected?.id || service.id}.jpg`,
        notes: [service.description, notes].filter(Boolean).join("\n\n"),
        serviceType: service.id,
        garmentId: selected?.id || "",
        startingPrice,
        requestDetails: {
          serviceId: service.id,
          startingPrice,
          selectedDesign: selected || {},
        },
        sourceDesign: selected || {},
      });
      setQuoteRequestId(request.id);
      setMessage("");
    } catch (error) {
      setMessage(error.message || "Unable to submit the quotation request.");
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = Boolean((selected || upload.data) && customer.name.trim() && isQuoteContactValid(quoteContact));
  return <div className="min-h-screen bg-[#FFF8F2]"><Navbar onBook={() => navigate("/services")} /><main className="pt-28 pb-24"><section className="max-w-[1200px] mx-auto px-6 md:px-12">
    <button onClick={() => navigate("/services")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]"><ArrowLeft size={16} /> Back to services</button>
    <div className="mt-8 max-w-3xl"><div className="font-label text-[#F46524]">Special Stitching</div><h1 className="font-display text-4xl md:text-6xl mt-3">Bring a distinctive idea to life</h1><p className="text-[#5b5a56] mt-5 text-lg">Choose a catalogue concept or upload a reference. StichB reviews the requirement and confirms the final quotation before work begins.</p></div>
    <div className="mt-10 flex flex-wrap gap-3" role="tablist">{SERVICES.map((item) => <button key={item.id} type="button" role="tab" aria-selected={service.id === item.id} onClick={() => setService(item)} className={`px-5 py-3 border font-semibold ${service.id === item.id ? "bg-[#6B1F2B] border-[#6B1F2B] text-white" : "bg-white border-[#E5D4C8] text-[#4d4540]"}`}>{item.label}</button>)}</div>
    <div className="mt-8"><h2 className="font-display text-3xl">{service.label}</h2><p className="text-[#5b5a56] mt-2">{service.description}</p></div>
    {service.collectionName ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">{items.length ? items.map((item) => <button key={item.id} type="button" onClick={() => setSelected(item)} className={`text-left bg-white border overflow-hidden ${selected?.id === item.id ? "border-[#F46524] ring-2 ring-[#F46524]" : "border-[#E5D4C8]"}`}><div className="aspect-[4/3] bg-[#F3E8DE]">{imageOf(item) ? <img src={imageOf(item)} alt={item.name || service.label} className="w-full h-full object-cover" /> : <span className="h-full flex items-center justify-center text-[#6B1F2B]"><Scissors size={36} /></span>}</div><div className="p-5"><div className="flex justify-between gap-3"><h3 className="font-display text-2xl">{item.name || service.label}</h3>{selected?.id === item.id ? <Check className="text-[#F46524] shrink-0" /> : null}</div>{item.description ? <p className="text-sm text-[#5b5a56] mt-2">{item.description}</p> : null}{item.basePrice ? <p className="text-sm font-semibold text-[#F46524] mt-3">Starting from ₹{Number(item.basePrice).toLocaleString("en-IN")}</p> : null}</div></button>) : <div className="card-soft p-7 sm:col-span-2 lg:col-span-3">No active designs are available yet. Upload your own reference below to request this service.</div>}</div> : null}<label className="mt-7 border-2 border-dashed border-[#D9BCA9] bg-white p-8 flex flex-col items-center text-center cursor-pointer"><Upload className="text-[#F46524]" /><strong className="mt-3">Upload {service.id === "embroidery" ? "embroidery" : "your own"} reference</strong><span className="text-sm text-[#6d625c] mt-1">JPG, PNG or WebP up to 8 MB</span><input type="file" accept="image/*" onChange={chooseUpload} className="sr-only" />{upload.name ? <span className="text-sm font-semibold text-green-700 mt-3">{upload.name}</span> : null}</label>
    <div className="bg-white border border-[#E5D4C8] p-6 mt-8"><h2 className="font-display text-2xl">Request quotation</h2><div className="grid md:grid-cols-2 gap-4 mt-5"><label className="text-sm font-semibold">Name<input value={customer.name} onChange={(event) => setCustomer({ ...customer, name: event.target.value })} className="w-full mt-2 border border-[#D9C9BE] p-3" /></label><label className="text-sm font-semibold">Email<input type="email" value={customer.email} onChange={(event) => setCustomer({ ...customer, email: event.target.value })} className="w-full mt-2 border border-[#D9C9BE] p-3" /></label></div><div className="mt-5"><TailorQuoteContactFields value={quoteContact} onChange={setQuoteContact} /></div><label className="block text-sm font-semibold mt-4">Requirements<textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={4} className="w-full mt-2 border border-[#D9C9BE] p-3" placeholder="Sizes, participants, preferred model, embroidery area, deadline or other instructions" /></label><button type="button" disabled={busy || Boolean(quoteRequestId) || (user && !canSubmit)} onClick={submit} className="btn-primary mt-5 disabled:opacity-50">{busy ? "Submitting…" : quoteRequestId ? "Quotation requested" : user ? "Request quotation" : "Sign in to continue"}</button>{message ? <p className="text-sm mt-4 text-[#6B1F2B]">{message}</p> : null}{quoteRequestId ? <div className="mt-5 border border-green-200 bg-green-50 p-5 text-sm"><p className="font-semibold text-green-800">Quotation request saved</p><p className="mt-2 text-[#5b5a56]">You can safely leave this page. Find this request under Orders → Quotations and continue after the tailor calls you.</p><button type="button" onClick={() => navigate(`/account/quotes/${quoteRequestId}`)} className="mt-3 font-semibold text-[#F46524]">View saved quotation</button></div> : null}</div>
  </section></main><Footer /></div>;
}